import React, { useEffect, useState, useCallback } from 'react';
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalCloseButton,
	ModalBody,
	ModalFooter,
	Button,
	Input,
	HStack,
	useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import config from '../config';
import globalStore from '../store';
import { UserDevice } from '@repo/alictus-common/types/user';

const AddDeviceIdModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
	const [deviceList, setDeviceList] = useState<UserDevice[]>([]);
	const [deviceName, setDeviceName] = useState('');
	const [deviceId, setDeviceId] = useState('');
	const [showSubmitButton, setShowSubmitButton] = useState(true);
	const toast = useToast();
	let globalData = globalStore();

	const resetForm = () => {
		setDeviceName('');
		setDeviceId('');
	};

	const fetchDeviceData = useCallback(async () => {
		let bearerToken = 'Bearer ' + globalData.JWTToken;
		await axios
			.get(config.API_ENDPOINT + '/device', { headers: { Authorization: bearerToken } })
			.then((response) => {
				setDeviceList(response.data as UserDevice[]);
			})
			.catch((error) => {
				console.error('Error fetching device data:', error);
				setDeviceList([]);
			});
	}, [isOpen]);

	const handleSubmit = async () => {
		console.log('handleSubmitCalled');
		console.log(deviceList);
		setShowSubmitButton(false);
		await axios
			.post(config.API_ENDPOINT + '/device', deviceList, { headers: { Authorization: 'Bearer ' + globalData.JWTToken } })
			.then((response) => {
				console.log(response);
				resetForm();
				setShowSubmitButton(true);
				onClose();
			});
	};

	useEffect(() => {
		if (isOpen) {
			fetchDeviceData().then((r) => console.log('Device Data Fetched'));
		}
	}, [isOpen, fetchDeviceData]);

	function updateDeviceId(index: number, device_id: string) {
		// Validate device ID using isNullOrEmpty function
		if (isNullOrEmpty(device_id)) {
			toast({
				title: 'Invalid Device ID',
				description: 'Device ID cannot be empty or contain only zeros.',
				status: 'warning',
				duration: 3000,
				isClosable: true,
			});
			return;
		}

		const temp = [...deviceList];
		temp[index].deviceId = device_id;
		setDeviceList(temp);
	}

	function updateDeviceName(index: number, device_name: string) {
		const temp = [...deviceList];
		temp[index].name = device_name;
		setDeviceList(temp);
	}

	function deleteDevice(index: number) {
		const temp = [...deviceList];
		temp.splice(index, 1);
		setDeviceList(temp);
	}

	function isNullOrEmpty(value: any): boolean {
		// Handle null, undefined, empty string, or single '0'
		if (value === null || value === undefined || value === '' || value === '0') {
			return true;
		}

		const stringValue = String(value).trim();

		// Check if it's all zeros with optional dashes/separators
		// This will catch: 0000, 000000, 00000000, 0000-0000, 00000000-0000-0000-0000-000000000000, etc.
		const isAllZeros = /^[0-]+$/.test(stringValue) && /0{4,}/.test(stringValue);

		return isAllZeros;
	}

	function addDevice() {
		// Validate device ID using isNullOrEmpty function
		if (isNullOrEmpty(deviceId)) {
			toast({
				title: 'Invalid Device ID',
				description: 'Please enter a valid device ID. Device ID cannot be empty or contain only zeros.',
				status: 'warning',
				duration: 3000,
				isClosable: true,
				position: 'top',
			});
			return;
		}

		// Validate device name
		if (!deviceName.trim()) {
			toast({
				title: 'Invalid Device Name',
				description: 'Please enter a valid device name.',
				status: 'warning',
				duration: 3000,
				isClosable: true,
				position: 'top',
			});
			return;
		}

		const temp = [...deviceList];
		temp.push({ name: deviceName, deviceId: deviceId, userId: 0 }); // userId is not used in the backend
		setDeviceName('');
		setDeviceId('');
		setDeviceList(temp);
		console.log(deviceList);
	}

	return (
		<Modal isOpen={isOpen} onClose={onClose}>
			<ModalOverlay />
			<ModalContent maxW="56rem">
				<ModalHeader>Add Device Id</ModalHeader>
				<ModalCloseButton />
				<form onSubmit={handleSubmit}>
					<ModalBody maxW={'600px'}>
						{deviceList &&
							deviceList.map((device, index) => (
								<HStack key={index} paddingBottom={'10px'} width={'850px'}>
									<Input width={'350px'} value={device.name} onChange={(event) => updateDeviceName(index, event.target.value)} />
									<Input width={'350px'} value={device.deviceId} onChange={(event) => updateDeviceId(index, event.target.value)} />
									<Button variant="outline" width={'150px'} colorScheme="red" mr={3} onClick={() => deleteDevice(index)}>
										Delete
									</Button>
								</HStack>
							))}
						<HStack paddingBottom={'10px'} width={'850px'}>
							<Input width={'350px'} placeholder="Device Name" value={deviceName} onChange={(event) => setDeviceName(event.target.value)} />
							<Input
								width={'350px'}
								placeholder="IOS/Android Device Id"
								value={deviceId}
								onChange={(event) => setDeviceId(event.target.value)}
							/>
							<Button variant="outline" width={'150px'} colorScheme="blue" mr={3} onClick={() => addDevice()}>
								Add
							</Button>
						</HStack>
					</ModalBody>
					<ModalFooter>
						<Button variant="outline" mr={3} isLoading={!showSubmitButton} onClick={() => handleSubmit()}>
							Submit
						</Button>
					</ModalFooter>
				</form>
			</ModalContent>
		</Modal>
	);
};

export default AddDeviceIdModal;
